import { Stack } from '@mui/material';
import Typography from '@mui/material/Typography';
import type { ComponentProps, FC } from 'react';

import { PortableText } from '~/components/atoms/PortableText';
import { Accordion } from '~/components/molecules/Accordion';
import type { FaqListProps } from '~/types/sanity/components';
import { getCompoundTestIdGenerator } from '~/utils/getCompoundTestIdGenerator';
import { isPortableTextBlockArray } from '~/utils/portableTextUtils';
import { formatStringForTestId } from '~/utils/stringUtils';

export const FAQContent: FC<Omit<FaqListProps, '_key' | '_type'>> = ({ faqItems, faqListTitle, id }) => {
  const getCompoundTestId = getCompoundTestIdGenerator('faqContent');
  const accordionItems: Required<Pick<ComponentProps<typeof Accordion>, 'item' | 'title' | 'id'>>[] = faqItems
    .map((faqItem) => {
      const formattedKey = formatStringForTestId(faqItem._key);

      if (!isPortableTextBlockArray(faqItem.faqContent)) {
        return null;
      }

      return {
        id: id ? `${id}-${formattedKey}` : formattedKey,
        title: faqItem.faqTitle,
        item: isPortableTextBlockArray(faqItem.faqContent) && <PortableText portableText={faqItem.faqContent} />,
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null); // Type guard filter

  return (
    <Stack data-testid={getCompoundTestId()}>
      <Typography variant="h5" sx={{ mb: 3 }} fontWeight={600}>
        {faqListTitle}
      </Typography>
      {accordionItems.map((props) => (
        <Accordion key={props.id} trackingContextId={id} {...props} />
      ))}
    </Stack>
  );
};
